import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, forkJoin, takeUntil } from 'rxjs';
import { userRoles } from '../../../environments/environment';
import { EventRadialChartComponent } from '../../shared/components/charts/radial-chart/radial-chart.component';
import { UniversalChartComponent } from '../../shared/components/charts/report-events/report-events.component';
import { TableComponent } from '../../shared/components/custom-table/table.component';
import { InputComponent } from '../../shared/components/from-input/input.component';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../shared/services/http.service';
import { TableExportService } from '../../shared/services/table-export.service';
import { ChartUtils } from '../../shared/utils/chart-utils';
import { ChartBuilderService } from '../../shared/services/chart-builder.service';
import { TableDataMapper } from '../../shared/utils/table-data-mapper';
import { DateUtils } from '../../shared/utils/date-utils';
import { MultiSelectComponent } from '../../shared/components/multi-select/multi-select.component';
import { Router } from '@angular/router';

interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}

interface ReportsDashboardPayload {
  from?: string;
  to?: string;
  regionId?: string | number;
  eventTypeId?: string | number;
  managerId?: string | number;
  eventManagerId?: string | number;
  mccPartnerId?: string | number;
  year?: string | number;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDialogModule,
    FormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatIconModule,
    MatNativeDateModule,
    InputComponent,
    UniversalChartComponent,
    TableComponent,
    MultiSelectComponent,
  ],
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    ChartBuilderService,
  ],
})
export class ReportsComponent implements OnInit, OnDestroy {
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  private readonly destroy$ = new Subject<void>();

  // ── cancel in-flight API call when tab changes ────────────────────
  private activeRequest$ = new Subject<void>();

  // ── chart instances (ApexCharts raw instances) ───────────────────
  private managerChartInstance: any = null;
  private regionChartInstance: any = null;

  // ── resize ───────────────────────────────────────────────────────
  private resizeTimer: any = null;
  private readonly RESIZE_DEBOUNCE = 250;

  // ── render-lock flags (prevent double-render race) ───────────────
  private managerRenderPending = false;
  private regionRenderPending = false;

  // ── pending render timeouts ──────────────────────────────────────
  private managerRenderTimer: any = null;
  private regionRenderTimer: any = null;

  // ── dashboard-load debounce ──────────────────────────────────────
  private dashboardDebounceTimer: any = null;
  private readonly DASHBOARD_DEBOUNCE = 400;

  // ── public state ─────────────────────────────────────────────────
  loading = true;
  activeTab = 'Reports dashboard';
  selectedDate: Date = new Date();
  startDate!: Date;
  endDate!: any;
  showExportDropdown = false;
  isExporting = false;

  mccPartners: any[] = [];

  selectedMccPartners: any[] = [];
  selectedYears: any[] = [];
  selectedEventTypes: any[] = [];
  selectedManagers: any[] = [];
  selectedMccPartnersList: any[] = [];

  selectedRegion: any = null;
  selectedYear: any = null;
  selectedEventType: any = null;
  selectedManager: any = null;
  selectedMccPartner: any = null;

  eventReportTable: string[] = [
    'Event name',
    'school',
    'date',
    'region',
    'status',
  ];
  eventTypeTable: string[] = [
    'Event type',
    'No. of events conducted',
    'No. of attendees',
    'Average attendance per event',
  ];
  totalEventsManaged: string[] = [
    'Event manager',
    'Total events',
    'Completed',
    'Upcoming',
    'Total attendees',
    'Average attendance per event',
  ];

  partnerEngagement: string[] = [
    'partner name',
    'Total events participated',
    'Events types engaged in',
    'Regions covered',
  ];
  engagementSchool: string[] = [
    'School name',
    'No. of events participated',
    'Total students registered',
    'Total students attended',
    'Most recent event date',
  ];
  industryPartnerTable: string[] = [
    'Partner name',
    'Total events participated',
    'Total attendees',
    'Event type',
    'Regions covered',
  ];

  eventManagers: any[] = [];
  eventTypes: any[] = [];
  regions: any[] = [];
  yearDropdownList: any[] = [];

  eventsBymanager: any = {};
  eventsByRegion: any = {};
  topBarChart: any;
  upcomingPieChart: any;
  completedPieChart: any;
  summaryData: any;
  TableData: any[] = [];

  attendanceTrendChart: any = null;
  attendanceSuccessRateChart: any = null;
  attendanceByEventTypeChart: any = null;

  diversityPieChart: any;
  genderDistributionChart: any;
  totalEthnicities = 0;
  ageDistributionChart: any;

  readonly titleMap: Record<string, string> = {
    'Reports dashboard': 'Upcoming vs Completed events (by month)',
    'Event report': 'Event count by type',
    'Event type report': 'Event type by focus area',
    'Manager activity': 'Total events managed',
    'Industry partner': 'Industry partners activity',
    'MCC event partner': 'Major Contractor & Subcontractor Partners Engagement',
    'School engagement': 'Top 10 engaged schools',
    Diversity: 'Diversity overview',
  };

  userRoles = {
    share: false,
    reportDashboard: false,
    eventTypeReport: false,
    eventReport: false,
    managerActivity: false,
    industryPartner: false,
    mccEvents: false,
    schoolEngagement: false,
  };
  currentTablePage: number = 1;
  pageSize: number = 25;
  totalTableItems: number = 0;
  fullTableData: any[] = [];
  paginatedTableData: any[] = [];
  originalTableData: any[] = [];

  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | '' = '';
  dataLoaded: boolean = false;
  appliedStartDate: Date | null = null;
  appliedEndDate: Date | null = null;
  showNoDataEventManagerChart: boolean = false;
  showNoDataEventRegionChart: boolean = false;
  showNoDataUpcomingEventChart: boolean = false;
  showNoDataTopBarChart: boolean = false;
  showEthnicityDistributionChart: boolean = true;
  showGenderDistributionChart: boolean = true;
  showAgeDistributionChart: boolean = true;
  showNoDataAttendanceTrendChart: boolean = false;
  showNoDataAttendanceSuccessRateChart: boolean = false;
  showNoDataAttendanceByEventTypeChart: boolean = false;
  private cancelRequest$ = new Subject<void>();
  readonly debouncedLoadDashboard = (reset = false) => {
    clearTimeout(this.dashboardDebounceTimer);
    this.dashboardDebounceTimer = setTimeout(() => {
      this.loadDashboardReports(reset);
    }, this.DASHBOARD_DEBOUNCE);
  };

  constructor(
    private dialog: MatDialog,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private location: Location,
    private exportService: TableExportService,
    private adapter: DateAdapter<Date>,
    private chartBuilder: ChartBuilderService,
    private router: Router,
  ) {
    this.adapter.setLocale('en-GB');
  }

  ngOnInit(): void {
    const navigation = this.location.getState() as { activeTab?: string };
    if (navigation?.activeTab) {
      this.activeTab = navigation.activeTab;
    }

    this.setDefaultDateRange();
    this.loadDropdownData();
    this.debouncedLoadDashboard(true);

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      if (user === 'csquser') {
        this.activeTab = 'Reports dashboard';
      }
      this.userRoles = (userRoles as any)[user]?.report;
    }
  }

  ngOnDestroy(): void {
    this.activeRequest$.next();
    this.activeRequest$.complete();
    this.destroy$.next();
    this.destroy$.complete();

    clearTimeout(this.dashboardDebounceTimer);
    clearTimeout(this.managerRenderTimer);
    clearTimeout(this.regionRenderTimer);
    clearTimeout(this.resizeTimer);

    window.removeEventListener('resize', this.onWindowResize);

    this.safeDestroyChart('manager');
    this.safeDestroyChart('region');
  }

  private readonly onWindowResize = (): void => {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.triggerApexChartsResize();
      this.resizeDomCharts();
      if (this.activeTab === 'Diversity') {
        this.refreshDiversityChartRefs();
      }
    }, this.RESIZE_DEBOUNCE);
  };

  private safeDestroyChart(type: 'manager' | 'region'): void {
    const inst =
      type === 'manager' ? this.managerChartInstance : this.regionChartInstance;
    if (inst) {
      try {
        inst.destroy();
      } catch (_) {
        /* ignore */
      }
      if (type === 'manager') {
        this.managerChartInstance = null;
      } else {
        this.regionChartInstance = null;
      }
    }
  }

  mapToOptions(data: any[], labelKey: string, valueKey: string): any[] {
    data = data?.filter(
      (item) => item.status === 'Active' || item.status == 'Active',
    );
    return (data || []).map((item) => ({
      label: item[labelKey],
      value: item[valueKey],
    }));
  }

  getMccPartners() {
    return this.httpService.get<ApiResponse<any[]>>(
      'mcceventpartners?status=2',
    );
  }

  loadDropdownData(): void {
    forkJoin({
      managers: this.getEventManagers(),
      types: this.getEventTypes(),
      regions: this.getEventHostRegions(),
      year: this.getReportsYear(),
      mccPartners: this.getMccPartners(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ managers, types, regions, year, mccPartners }) => {
          this.eventManagers = [
            { label: 'All', value: 'All' },
            ...(this.mapToOptions(managers?.data, 'fullName', 'memberId') ??
              []),
          ];

          this.eventTypes = [
            { label: 'All', value: 'All' },
            ...(this.mapToOptions(
              types?.data,
              'eventTypeName',
              'eventTypeId',
            ) ?? []),
          ];

          this.regions = [
            { label: 'All', value: 'All' },
            ...(this.mapToOptions(
              regions?.data,
              'eventHostRegionName',
              'eventHostRegionId',
            ) ?? []),
          ];

          this.yearDropdownList = [
            { label: 'All', value: 'All' },
            ...(year?.data ?? []).map((item) => ({
              label: item.toString(),
              value: item.toString(),
            })),
          ];

          this.mccPartners = [
            { label: 'All', value: 'All' },
            ...(this.mapToOptions(
              mccPartners?.data,
              'mccEventPartnerName',
              'mccEventPartnerId',
            ) ?? []),
          ];
          this.cdr.markForCheck();
        },
        error: () => this.showErrorToast('Failed to load report filters'),
      });
  }

  getEventManagers() {
    return this.httpService.get<ApiResponse<any[]>>(
      'events/event-managers?status=2',
    );
  }

  getEventTypes() {
    return this.httpService.get<ApiResponse<any[]>>('eventtypes?status=1');
  }

  getEventHostRegions() {
    return this.httpService.get<ApiResponse<any[]>>(
      'eventhostregions?status=1',
    );
  }

  getReportsYear() {
    return this.httpService.get<ApiResponse<any[]>>('reports/years');
  }

  setDefaultDateRange(): void {
    const today = new Date();
    this.endDate = today;
    const januaryFirst = new Date(today.getFullYear(), 0, 1);
    this.startDate = januaryFirst;
    this.selectedDate = this.startDate;

    this.selectedMccPartners = [{ label: 'All', value: 'All' }];
    this.selectedEventTypes = [{ label: 'All', value: 'All' }];
    this.selectedManagers = [{ label: 'All', value: 'All' }];
    this.selectedYears = [{ label: 'All', value: 'All' }];
    this.selectedMccPartnersList = [{ label: 'All', value: 'All' }];

    this.selectedRegion = null;
    this.selectedEventType = null;
    this.selectedManager = null;
    this.selectedYear = null;
    this.selectedMccPartner = null;
  }
  private handleAttendanceTrendAnalysisResponse(res: any): void {
    if (res?.data) {
      if (
        res.data.attendencetradeovertime?.length &&
        res.data.attendencetradeovertime[0]?.series?.length
      ) {
        this.attendanceTrendChart =
          this.chartBuilder.buildAttendanceTrendLineChart(
            res.data.attendencetradeovertime,
            this.startDate,
            this.endDate,
          );
        this.showNoDataAttendanceTrendChart = false;
      } else {
        this.attendanceTrendChart = null;
        this.showNoDataAttendanceTrendChart = true;
      }
      if (
        res.data.attendenceRate &&
        (res.data.attendenceRate.attend > 0 ||
          res.data.attendenceRate.absent > 0)
      ) {
        this.attendanceSuccessRateChart =
          this.chartBuilder.buildAttendanceSuccessRateChart(
            res.data.attendenceRate.attend || 0,
            res.data.attendenceRate.absent || 0,
          );
        this.showNoDataAttendanceSuccessRateChart = false;
      } else {
        this.attendanceSuccessRateChart =
          this.chartBuilder.buildAttendanceSuccessRateChart(0, 0);
        this.showNoDataAttendanceSuccessRateChart = true;
      }
      if (res.data.monthWiseAttendance?.length) {
        this.attendanceByEventTypeChart =
          this.chartBuilder.buildAttendanceByEventTypeChart(
            res.data.monthWiseAttendance,
          );
        this.showNoDataAttendanceByEventTypeChart = false;
      } else {
        this.attendanceByEventTypeChart = null;
        this.showNoDataAttendanceByEventTypeChart = true;
      }
    } else {
      this.resetAllChartsToNoData();
    }

    this.cdr.markForCheck();
  }

  private resetAllChartsToNoData(): void {
    this.attendanceTrendChart = null;
    this.attendanceSuccessRateChart = null;
    this.attendanceByEventTypeChart = null;
    this.showNoDataAttendanceTrendChart = true;
    this.showNoDataAttendanceSuccessRateChart = true;
    this.showNoDataAttendanceByEventTypeChart = true;
  }

  setActive(tab: string): void {
    if (this.activeTab === tab) return;
    this.activeRequest$.next();

    this.clearAllChartState();

    this.safeDestroyChart('manager');
    this.safeDestroyChart('region');

    this.sortColumn = null;
    this.sortDirection = '';

    this.resetFilters();

    this.activeTab = tab;
    this.debouncedLoadDashboard(true);
  }

  private resetFilters(): void {
    this.originalTableData = [];
    this.selectedMccPartners = [];
    this.selectedYears = [];
    this.selectedEventTypes = [];
    this.selectedManagers = [];
    this.selectedMccPartnersList = [];
    this.selectedRegion = null;
    this.selectedYear = null;
    this.selectedEventType = null;
    this.selectedManager = null;
    this.selectedMccPartner = null;
    this.setDefaultDateRange();
    this.currentTablePage = 1;
    this.fullTableData = [];
    this.paginatedTableData = [];
    this.totalTableItems = 0;
    this.cdr.markForCheck();
  }

  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.showExportDropdown = !this.showExportDropdown;
  }

  closeStatusDropdown(): void {
    this.showExportDropdown = false;
  }

  goBack(): void {
    const role = localStorage.getItem('userRole');
    const roleRouteMap: Record<string, string> = {
      superadmin: '/dashboard/super-admin',
      csqadmin: '/dashboard/csq-admin',
      csquser: '/dashboard/csq-user',
      teacher: '/dashboard/teacher',
      student: '/dashboard/user',
    };
    const route = (role && roleRouteMap[role]) || '/dashboard/super-admin';
    this.router.navigate([route]);
  }

  getMinEndDate(): string | null {
    if (!this.startDate) return null;
    const date = new Date(this.startDate);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  }

  get activeTabClass(): string {
    return this.activeTab?.toLowerCase().trim().replace(/\s+/g, '-');
  }

  onStartDateChange(dateString: string): void {
    if (!dateString) return;
    this.startDate = new Date(dateString);
    if (this.endDate && this.endDate <= this.startDate) {
      const nextDay = new Date(this.startDate);
      nextDay.setDate(nextDay.getDate() + 1);
      this.endDate = nextDay;
    }
    this.cdr.markForCheck();
  }

  onEndDateChange(dateString: string): void {
    if (!dateString) return;
    this.endDate = new Date(dateString);
    if (this.startDate && this.endDate <= this.startDate) {
      this.showErrorToast('End date must be after start date');
      return;
    }
    this.cdr.markForCheck();
  }

  onRegionChange(selectedValues: any[]): void {
    this.selectedMccPartners = selectedValues;

    const hasAll = selectedValues?.some(
      (v) => v?.value === 'All' || v === 'All',
    );

    this.selectedRegion =
      selectedValues && selectedValues.length > 0 && !hasAll
        ? selectedValues
        : null;
  }

  onEventTypeChange(selectedValues: any[]): void {
    this.selectedEventTypes = selectedValues;
    const hasAll = selectedValues?.some(
      (v) => v?.value === 'All' || v === 'All',
    );
    this.selectedEventType =
      selectedValues?.length > 0 && !hasAll ? selectedValues : null;
  }

  onManagerChange(selectedValues: any[]): void {
    this.selectedManagers = selectedValues;
    const hasAll = selectedValues?.some(
      (v) => v?.value === 'All' || v === 'All',
    );
    this.selectedManager =
      selectedValues?.length > 0 && !hasAll ? selectedValues : null;
  }

  onYearChange(selectedValues: any[]): void {
    this.selectedYears = selectedValues;
    const hasAll = selectedValues?.some(
      (v) => v?.value === 'All' || v === 'All',
    );
    this.selectedYear =
      selectedValues?.length > 0 && !hasAll ? selectedValues : null;
  }

  onMccPartnerChange(selectedValues: any[]): void {
    this.selectedMccPartnersList = selectedValues;
    const hasAll = selectedValues?.some(
      (v) => v?.value === 'All' || v === 'All',
    );
    this.selectedMccPartner =
      selectedValues?.length > 0 && !hasAll ? selectedValues : null;
  }

  applyFilter(): void {
    if (this.activeTab !== 'School engagement') {
      if (!this.startDate || !this.endDate) return;
      if (this.endDate <= this.startDate) {
        this.showErrorToast('End date must be after start date');
        return;
      }
    }
    this.sortColumn = null;
    this.sortDirection = '';
    this.loadDashboardReports();
  }

  resetFilter(): void {
    this.onReset();
    this.debouncedLoadDashboard(true);
  }

  onReset(): void {
    this.sortColumn = null;
    this.sortDirection = '';
    this.setDefaultDateRange();

    this.clearAllChartState();
    this.safeDestroyChart('manager');
    this.safeDestroyChart('region');

    this.cdr.markForCheck();
  }

  private clearAllChartState(): void {
    this.TableData = [];
    this.fullTableData = [];
    this.paginatedTableData = [];
    this.originalTableData = [];
    this.totalTableItems = 0;
    this.currentTablePage = 1;
    this.topBarChart = null;
    this.summaryData = null;
    this.eventsBymanager = {};
    this.eventsByRegion = {};
    this.attendanceTrendChart = null;
    this.attendanceSuccessRateChart = null;
    this.attendanceByEventTypeChart = null;
    this.diversityPieChart = null;
    this.genderDistributionChart = null;
    this.ageDistributionChart = null;
    this.completedPieChart = null;
    this.upcomingPieChart = null;
    this.totalEthnicities = 0;
    this.showEthnicityDistributionChart = true;
    this.showGenderDistributionChart = true;
    this.showAgeDistributionChart = true;
    this.showNoDataTopBarChart = false;
    this.showNoDataEventManagerChart = false;
    this.showNoDataEventRegionChart = false;
    this.showNoDataUpcomingEventChart = false;
    this.showNoDataAttendanceTrendChart = false;
    this.showNoDataAttendanceSuccessRateChart = false;
    this.showNoDataAttendanceByEventTypeChart = false;
    this.dataLoaded = false;
    this.appliedStartDate = null;
    this.appliedEndDate = null;
    this.cdr.markForCheck();
  }

  // ================================================================
  // DATA LOADING
  // ================================================================

  getCurrentTableColumns(): string[] {
    switch (this.activeTab) {
      case 'Event report':
        return this.eventReportTable;
      case 'Event type report':
        return this.eventTypeTable;
      case 'Manager activity':
        return this.totalEventsManaged;
      case 'Industry partner':
        return this.industryPartnerTable;
      case 'MCC event partner':
        return this.partnerEngagement;
      case 'School engagement':
        return this.engagementSchool;
      default:
        return [];
    }
  }

  loadDashboardReports(reset = false): void {
    if (this.activeTab !== 'School engagement') {
      if (!this.startDate || !this.endDate) {
        this.loading = false;
        this.cdr.markForCheck();
        return;
      }
      if (this.endDate <= this.startDate) {
        this.loading = false;
        this.showErrorToast('End date must be after start date');
        this.cdr.markForCheck();
        return;
      }
    }

    this.activeRequest$.next();
    this.loading = true;
    this.cdr.markForCheck();

    const payload = this.buildDashboardPayload();

    if (reset) {
      this.onReset();
      this.loading = true;
    }

    const requestedTab = this.activeTab;
    this.cancelRequest$.next();

    this.getDashboardReports(payload)
      .pipe(takeUntil(this.cancelRequest$), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (this.activeTab !== requestedTab) return;

          this.loading = false;
          this.dataLoaded = true;
          this.appliedStartDate = this.startDate
            ? new Date(this.startDate)
            : null;
          this.appliedEndDate = this.endDate ? new Date(this.endDate) : null;
          this.handleReportResponse(res);
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.loading = false;
          this.showErrorToast('Failed to load dashboard reports');
          this.cdr.markForCheck();
        },
      });
  }

  private handleReportResponse(res: any): void {
    switch (this.activeTab) {
      case 'Reports dashboard':
        this.handleDashboardResponse(res);
        break;
      case 'Event report':
        this.handleEventReportResponse(res);
        break;
      case 'Event type report':
        this.handleEventTypeReportResponse(res);
        break;
      case 'Manager activity':
        this.handleManagerActivityResponse(res);
        break;
      case 'Industry partner':
        this.handleIndustryPartnerActivityResponse(res);
        break;
      case 'MCC event partner':
        this.handleMccPartnerResponse(res);
        break;
      case 'School engagement':
        this.handleSchoolEngagementResponse(res);
        break;
      case 'Diversity':
        this.handleDiversityResponse(res);
        break;
      case 'Attendance trend analysis':
        this.handleAttendanceTrendAnalysisResponse(res);
        break;
      default:
        console.warn(`Unknown activeTab: ${this.activeTab}`);
    }
  }

  private handleDashboardResponse(res: any): void {
    this.summaryData = res.data.summary;

    if (res.data.eventsByMonth && res.data.eventsByMonth.length > 0) {
      this.buildMonthEventChart(
        this.transformMonthEvent(res.data.eventsByMonth),
      );
      this.showNoDataTopBarChart = false;
      this.showNoDataUpcomingEventChart = false;
    } else {
      this.showNoDataTopBarChart = true;
      this.showNoDataUpcomingEventChart = true;
      this.topBarChart = null;
    }
    if (res.data.eventsByManager && res.data.eventsByManager.length > 0) {
      this.scheduleManagerChart(res.data.eventsByManager);
      this.showNoDataEventManagerChart = false;
    } else {
      this.showNoDataEventManagerChart = true;
    }

    if (res.data.eventsByRegion && res.data.eventsByRegion.length > 0) {
      this.scheduleRegionChart(res.data.eventsByRegion);
      this.showNoDataEventRegionChart = false;
    } else {
      this.showNoDataEventRegionChart = true;
    }
  }

  onTablePageChange(page: number): void {
    this.currentTablePage = page;
    this.updatePaginatedData();
    this.cdr.markForCheck();
  }

  onSortChange(event: {
    direction: 'asc' | 'desc' | '';
    column: string | null;
  }): void {
    if (!event.column) {
      this.sortColumn = null;
      this.sortDirection = '';
    } else {
      this.sortColumn = event.column;
      this.sortDirection = event.direction;
    }
    this.sortTableData();
    this.currentTablePage = 1;
    this.updatePaginatedData();
    this.cdr.markForCheck();
  }

  private sortTableData(): void {
    if (!this.sortColumn || !this.sortDirection) {
      this.fullTableData = [...this.originalTableData];
      return;
    }

    if (this.fullTableData.length === 0) return;

    const isDateColumn = (columnName: string): boolean => {
      const dateColumnPatterns = [
        'date',
        'event date',
        'most recent event date',
      ];
      const lowerCol = columnName.toLowerCase();
      return dateColumnPatterns.some((pattern) => lowerCol.includes(pattern));
    };

    const parseDDMMYYYY = (dateStr: string): number | null => {
      if (!dateStr || typeof dateStr !== 'string') return null;
      const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!match) return null;
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const year = parseInt(match[3], 10);
      const date = new Date(year, month, day);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month &&
        date.getDate() === day
      ) {
        return date.getTime();
      }
      return null;
    };

    this.fullTableData.sort((a, b) => {
      let aVal = a[this.sortColumn!];
      let bVal = b[this.sortColumn!];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle date columns
      if (isDateColumn(this.sortColumn!)) {
        const aTimestamp = parseDDMMYYYY(String(aVal));
        const bTimestamp = parseDDMMYYYY(String(bVal));

        if (aTimestamp != null && bTimestamp != null) {
          return this.sortDirection === 'asc'
            ? aTimestamp - bTimestamp
            : bTimestamp - aTimestamp;
        }
      }

      // Handle numeric values
      const aNum = typeof aVal === 'number' ? aVal : Number(aVal);
      const bNum = typeof bVal === 'number' ? bVal : Number(bVal);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return this.sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string values
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (aStr < bStr) return this.sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  private updatePaginatedData(): void {
    const startIndex = (this.currentTablePage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    // Use fullTableData which is already sorted
    this.paginatedTableData = this.fullTableData.slice(startIndex, endIndex);
  }

  private handleEventReportResponse(res: any): void {
    this.fullTableData = TableDataMapper.mapEventReport(res.data.events);
    this.originalTableData = [...this.fullTableData];
    this.sortColumn = null;
    this.sortDirection = '';
    this.sortTableData();
    this.TableData = [...this.fullTableData];
    this.totalTableItems = this.fullTableData.length;
    this.currentTablePage = 1;
    this.updatePaginatedData();
    if (res.data.eventCountsByType && res.data.eventCountsByType.length > 0) {
      this.buildEventCountByTypeChart(res.data.eventCountsByType);
      this.showNoDataTopBarChart = false;
    } else {
      this.showNoDataTopBarChart = true;
    }
  }

  private handleEventTypeReportResponse(res: any): void {
    this.fullTableData = TableDataMapper.mapEventTypeReport(res.table);
    this.originalTableData = [...this.fullTableData];
    this.sortColumn = null;
    this.sortDirection = '';
    this.sortTableData();
    this.totalTableItems = this.fullTableData.length;
    this.currentTablePage = 1;
    this.updatePaginatedData();
    this.topBarChart = res.chart;
    if (res.chart && res.chart.length > 0) {
      this.buildEventTypeChart(res.chart);
      this.showNoDataTopBarChart = false;
    } else {
      this.showNoDataTopBarChart = true;
    }
  }

  private handleManagerActivityResponse(res: any): void {
    this.fullTableData = TableDataMapper.mapManagerActivity(res.tableData);
    this.originalTableData = [...this.fullTableData];
    this.sortColumn = null;
    this.sortDirection = '';
    this.sortTableData();
    this.totalTableItems = this.fullTableData.length;
    this.currentTablePage = 1;
    this.updatePaginatedData();
    this.topBarChart = res.graphData;
    if (res.graphData && res.graphData.length > 0) {
      this.buildEventsManagedChart(res.graphData);
      this.showNoDataTopBarChart = false;
    } else {
      this.showNoDataTopBarChart = true;
    }
  }

  private handleIndustryPartnerActivityResponse(res: any): void {
    this.fullTableData = TableDataMapper.mapIndustryPartnerActivity(
      res.tableData,
    ).sort(
      (a, b) =>
        (b['Total events participated'] ?? 0) -
        (a['Total events participated'] ?? 0),
    );
    this.originalTableData = [...this.fullTableData];
    this.sortColumn = null;
    this.sortDirection = '';
    this.sortTableData();
    this.totalTableItems = this.fullTableData.length;
    this.currentTablePage = 1;
    this.updatePaginatedData();
    this.topBarChart = res.graphData;
    if (res.graphData && res.graphData.length > 0) {
      this.buildIndustryPartnerChart(res.graphData);
      this.showNoDataTopBarChart = false;
    } else {
      this.showNoDataTopBarChart = true;
    }
    this.buildIndustryPartnerChart(res.graphData);
  }

  private handleMccPartnerResponse(res: any): void {
    this.fullTableData = TableDataMapper.mapMccPartner(res.mccPartners);
    this.originalTableData = [...this.fullTableData];
    this.sortColumn = null;
    this.sortDirection = '';
    this.sortTableData();
    this.totalTableItems = this.fullTableData.length;
    this.currentTablePage = 1;
    this.updatePaginatedData();
    this.buildPartnerChart(res.barChartData);
  }

  private handleSchoolEngagementResponse(res: any): void {
    this.fullTableData = TableDataMapper.mapSchoolEngagement(res.schools);
    this.originalTableData = [...this.fullTableData];
    this.sortColumn = null;
    this.sortDirection = '';
    this.sortTableData();
    this.totalTableItems = this.fullTableData.length;
    this.currentTablePage = 1;
    this.updatePaginatedData();
    if (
      res.graphDataEventParticipation &&
      res.graphDataEventParticipation.length > 0
    ) {
      this.buildEngagedSchoolChart(res);
      this.showNoDataTopBarChart = false;
    } else {
      this.showNoDataTopBarChart = true;
    }
  }

  private handleDiversityResponse(res: any): void {
    const ethnicityData = res?.data?.ethnicities;
    const genderData = res?.data?.genders;
    const ageData = res?.data?.dateOfBirths;

    this.totalEthnicities = ethnicityData?.length || 0;
    if (ethnicityData && ethnicityData.length > 0) {
      this.diversityPieChart =
        this.chartBuilder.buildDiversityPieChart(ethnicityData);
      this.showEthnicityDistributionChart = true;
    } else {
      this.diversityPieChart = null;
      this.showEthnicityDistributionChart = false;
    }

    if (genderData && genderData.length > 0) {
      this.genderDistributionChart =
        this.chartBuilder.buildGenderDistributionChart(genderData);
      this.showGenderDistributionChart = true;
    } else {
      this.genderDistributionChart = null;
      this.showGenderDistributionChart = false;
    }

    if (ageData && ageData.length > 0) {
      this.ageDistributionChart =
        this.chartBuilder.buildAgeDistributionChart(ageData);
      this.showAgeDistributionChart = true;
    } else {
      this.ageDistributionChart = null;
      this.showAgeDistributionChart = false;
    }

    this.cdr.detectChanges();

    this.ensureResizeListener();
  }

  // ================================================================
  // PAYLOAD
  // ================================================================

  buildDashboardPayload(): any {
    const isAll = (arr: any[]) =>
      arr?.some((v) => v?.value === 'All' || v === 'All');

    let regionIdValue = null;
    if (
      this.selectedMccPartners?.length > 0 &&
      !isAll(this.selectedMccPartners)
    ) {
      regionIdValue = this.selectedMccPartners;
    }

    let eventTypeIdValue = null;
    if (
      this.selectedEventTypes?.length > 0 &&
      !isAll(this.selectedEventTypes)
    ) {
      eventTypeIdValue = this.selectedEventTypes;
    }

    let managerIdValue = null;
    if (this.selectedManagers?.length > 0 && !isAll(this.selectedManagers)) {
      managerIdValue = this.selectedManagers;
    }

    let mccPartnerIdValue = null;
    if (
      this.selectedMccPartnersList?.length > 0 &&
      !isAll(this.selectedMccPartnersList)
    ) {
      mccPartnerIdValue = this.selectedMccPartnersList;
    }

    let yearValue = null;
    if (this.selectedYears?.length > 0 && !isAll(this.selectedYears)) {
      yearValue = this.selectedYears.map((y) => y.toString());
    }

    const datePayload =
      this.startDate && this.endDate
        ? {
            from: DateUtils.formatToISO(this.startDate),
            to: DateUtils.formatToISO(this.endDate),
          }
        : {};

    const basePayload = {
      ...datePayload,
      regionId: regionIdValue,
      eventTypeId: eventTypeIdValue,
      managerId: managerIdValue,
      eventManagerId: managerIdValue,
      mccPartnerId: mccPartnerIdValue,
    };

    if (this.activeTab === 'School engagement') {
      return {
        ...datePayload,
        year: yearValue,
        regionId: regionIdValue,
        eventTypeId: eventTypeIdValue,
        managerId: managerIdValue,
        eventManagerId: managerIdValue,
        mccPartnerId: mccPartnerIdValue,
      };
    }

    return basePayload;
  }

  getDashboardReports(payload: ReportsDashboardPayload) {
    const endpoints: Record<string, string> = {
      'Reports dashboard': 'reports/dashboard',
      'Event report': 'reports/event-report',
      'Event type report': 'reports/event-type-report',
      'Manager activity': 'reports/manager-activity-report',
      'Industry partner': 'reports/industry-partner-report',
      'MCC event partner': 'reports/mcc-event-partner-report',
      'School engagement': 'reports/school-engagement-report',
      Diversity: 'reports/diversity-report',
      'Attendance trend analysis': 'reports/attendence-trade-report',
    };
    return this.httpService.post(endpoints[this.activeTab], payload);
  }

  // ================================================================
  // CHART BUILD METHODS  (set chart-config objects on component)
  // ================================================================

  buildMonthEventChart(data: any[]): void {
    this.topBarChart = this.chartBuilder.buildMonthEventChart(
      data,
      this.startDate,
      this.endDate,
    );
  }

  buildEventCountByTypeChart(response: any[]): void {
    this.topBarChart = this.chartBuilder.buildEventCountByTypeChart(response);
  }

  buildEventTypeChart(response: any[]): void {
    this.topBarChart = this.chartBuilder.buildEventTypeChart(response);
  }

  buildPartnerChart(
    data: { partnerName: string; eventsParticipated: number }[],
  ): void {
    this.topBarChart = this.chartBuilder.buildPartnerChart(data);
  }

  buildIndustryPartnerChart(response: any[]): void {
    this.topBarChart = this.chartBuilder.buildIndustryPartnerChart(response);
  }

  buildEngagedSchoolChart(apiData: {
    graphDataEventParticipation: { label: string; value: number }[];
    graphDataEngagementBySchool: { label: string; value: number }[];
  }): void {
    this.topBarChart = this.chartBuilder.buildEngagedSchoolChart(apiData);
  }

  buildEventsManagedChart(response: any[]): void {
    this.topBarChart = this.chartBuilder.buildEventsManagedChart(response);
  }

  private scheduleManagerChart(data: any[]): void {
    this.eventsBymanager = this.chartBuilder.buildHorizontalBarChart(
      data,
      'managerName',
      'upcoming',
      'completed',
    );

    if (this.managerRenderPending) return;
    this.managerRenderPending = true;

    clearTimeout(this.managerRenderTimer);
    this.managerRenderTimer = setTimeout(() => {
      this.managerRenderPending = false;
      this.renderDomChart('manager-chart', this.eventsBymanager, 'manager');
    }, 120);
  }

  private scheduleRegionChart(data: any[]): void {
    this.eventsByRegion = this.chartBuilder.buildHorizontalBarChart(
      data,
      'region',
      'upcoming',
      'completed',
    );

    if (this.regionRenderPending) return;
    this.regionRenderPending = true;

    clearTimeout(this.regionRenderTimer);
    this.regionRenderTimer = setTimeout(() => {
      this.regionRenderPending = false;
      this.renderDomChart('region-chart', this.eventsByRegion, 'region');
    }, 120);
  }

  buildManagerChart(response: any[]): void {
    this.scheduleManagerChart(response);
  }

  buildRegionChart(response: any[]): void {
    this.scheduleRegionChart(response);
  }

  private renderDomChart(
    elementId: string,
    chartData: any,
    type: 'manager' | 'region',
  ): void {
    if (!chartData?.series) return;

    const chartElement = document.querySelector(`#${elementId}`);
    if (!chartElement) {
      return;
    }

    this.safeDestroyChart(type);

    try {
      const chart = new (window as any).ApexCharts(chartElement, chartData);
      chart.render();

      if (type === 'manager') {
        this.managerChartInstance = chart;
      } else {
        this.regionChartInstance = chart;
      }

      this.ensureResizeListener();
    } catch (error) {
      console.warn(`Error rendering #${elementId}:`, error);
    }
  }

  private ensureResizeListener(): void {
    window.removeEventListener('resize', this.onWindowResize);
    window.addEventListener('resize', this.onWindowResize);
  }

  private resizeDomCharts(): void {
    [
      { inst: this.managerChartInstance },
      { inst: this.regionChartInstance },
    ].forEach(({ inst }) => {
      if (!inst) return;
      try {
        const categories: string[] = inst.w?.config?.xaxis?.categories ?? [];
        const h = Math.min(600, Math.max(350, categories.length * 35));
        inst.updateOptions({ chart: { height: h } }, false, false);
      } catch (e) {
        console.warn('Chart resize error:', e);
      }
    });
  }

  triggerApexChartsResize(): void {
    document.querySelectorAll('apx-chart').forEach((el: any) => {
      if (!el?.chart) return;
      try {
        el.chart.updateOptions(
          {
            chart: {
              ...el.chart.opts?.chart,
              width: el.parentElement?.offsetWidth || '100%',
            },
          },
          false,
          true,
        );
      } catch (_) {}
    });
  }

  private refreshDiversityChartRefs(): void {
    setTimeout(() => {
      if (this.diversityPieChart) {
        this.diversityPieChart = { ...this.diversityPieChart };
      }
      if (this.genderDistributionChart) {
        this.genderDistributionChart = { ...this.genderDistributionChart };
      }
      this.cdr.markForCheck();
    }, 100);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.onWindowResize();
  }

  transformMonthEvent(apiData: any[]): any[] {
    return ChartUtils.transformMonthEvent(apiData);
  }

  formatDate(dateStr: string): string {
    return DateUtils.formatDisplayDate(dateStr);
  }

  formatDateItem(date: Date): string {
    return DateUtils.formatToISO(date);
  }

  debounce<T extends (...args: any[]) => void>(
    fn: T,
    delay = 300,
  ): (...args: Parameters<T>) => void {
    let timer: any;
    return (...args: Parameters<T>) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  getCSVDataByActiveTab(): any[][] {
    const exportData =
      this.fullTableData.length > 0 ? this.fullTableData : this.TableData;

    switch (this.activeTab) {
      case 'Event report':
        return exportData.map((e: any) => [
          e['Event name'] || e['eventName'] || '',
          e['Event type'] || e['eventType'] || '',
          e['date'] || '',
          e['region'] || '',
          e['status'] || '',
        ]);
      case 'Event type report':
        return exportData.map((e: any) => [
          e['Event type'] || e['eventType'] || '',
          e['No. of events conducted'] ?? e['eventsConducted'] ?? '',
          e['No. of attendees'] ?? e['attendees'] ?? '',
          e['Average attendance per event'] ?? e['averageAttendance'] ?? '',
        ]);

      case 'Manager activity':
        return exportData.map((m: any) => [
          m['Event manager'] || m['managerName'] || '',
          m['Total events'] ?? m['totalEvents'] ?? '',
          m['Completed'] ?? m['completed'] ?? '',
          m['Upcoming'] ?? m['upcoming'] ?? '',
          m['Total attendees'] ?? m['totalAttendees'] ?? '',
          m['Average attendance per event'] ?? m['averageAttendance'] ?? '',
        ]);

      case 'MCC event partner':
        return exportData.map((p: any) => [
          p['partner name'] || p['partnerName'] || '',
          p['Total events participated'] ?? p['totalEvents'] ?? '',
          p['Events types engaged in'] || p['eventTypes'] || '',
          p['Regions covered'] || p['regions'] || '',
        ]);

      case 'School engagement':
        return exportData.map((s: any) => [
          s['School name'] || s['schoolName'] || '',
          s['No. of events participated'] ?? s['eventsParticipated'] ?? '',
          s['Total students registered'] ?? s['registeredStudents'] ?? '',
          s['Total students attended'] ?? s['attendedStudents'] ?? '',
          s['Most recent event date'] || s['recentEventDate'] || '',
        ]);

      case 'Industry partner':
        return exportData.map((p: any) => [
          p['Partner name'] || p['partnerName'] || '',
          p['Total events participated'] ?? p['totalEvents'] ?? '',
          p['Total attendees'] ?? p['totalAttendees'] ?? '',
          p['Event type'] || p['eventType'] || '',
          p['Regions covered'] || p['regions'] || '',
        ]);
      default:
        return [];
    }
  }

  exportTable(type: 'pdf' | 'csv'): void {
    if (this.isExporting) return;
    if (!this.fullTableData || this.fullTableData.length === 0) {
      this.showErrorToast('No data available to export');
      return;
    }

    this.isExporting = true;

    try {
      const headers = this.getExportHeaders();
      const exportData = this.getCSVDataByActiveTab();

      if (exportData.length === 0) {
        this.showErrorToast('No data to export');
        this.isExporting = false;
        return;
      }

      const fileName = `${this.activeTab.replace(/\s+/g, '-').toLowerCase()}-export`;

      // Generate the date string
      const generatedDate = this.getFormattedDate();

      if (type === 'csv') {
        this.exportService.exportTable(
          'csv',
          headers,
          exportData,
          fileName,
          undefined,
          generatedDate,
        );
      } else if (type === 'pdf') {
        this.exportService.exportTable(
          'pdf',
          headers,
          exportData,
          fileName,
          undefined,
          generatedDate,
        );
      }

      this.closeStatusDropdown();
    } catch (error) {
      console.error('Export error:', error);
      this.showErrorToast('Failed to export data');
    } finally {
      setTimeout(() => {
        this.isExporting = false;
        this.cdr.markForCheck();
      }, 1000);
    }
  }

  // Add this helper method to your component
  private getFormattedDate(): string {
    const date = new Date();
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    // Add ordinal suffix to day
    const ordinal = (n: number): string => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `Generated on ${ordinal(day)} ${month}, ${year}`;
  }

  private getExportHeaders(): string[] {
    switch (this.activeTab) {
      case 'Event report':
        return ['Event name', 'Event type', 'Date', 'Region', 'Status'];
      case 'Event type report':
        return [
          'Event type',
          'No. of events conducted',
          'No. of attendees',
          'Average attendance per event',
        ];
      case 'Manager activity':
        return [
          'Event manager',
          'Total events',
          'Completed',
          'Upcoming',
          'Total attendees',
          'Average attendance per event',
        ];
      case 'MCC event partner':
        return [
          'Partner name',
          'Total events participated',
          'Events types engaged in',
          'Regions covered',
        ];
      case 'School engagement':
        return [
          'School name',
          'No. of events participated',
          'Total students registered',
          'Total students attended',
          'Most recent event date',
        ];
      case 'Industry partner':
        return [
          'Partner name',
          'Total events participated',
          'Total attendees',
          'Event type',
          'Regions covered',
        ];
      default:
        return [];
    }
  }

  // ================================================================
  // TOAST
  // ================================================================

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
